/** Zugang zum Server.
 *
 *  Es gibt zwei Betriebsarten, damit die App nie an einer fehlenden Datenbank
 *  scheitert:
 *
 *    "server"  — der Worker antwortet. Alles funktioniert, Änderungen bleiben.
 *    "vorschau"— kein Worker erreichbar. Die Bücher kommen aus books.json,
 *                die Ansicht funktioniert, jede Änderung wird deutlich
 *                abgelehnt statt still verworfen.
 *
 *  Der Modus wird einmal beim Start ermittelt und ist über useApiMode() abrufbar.
 */

import type {
  Book,
  BookList,
  Owner,
  Recommendation,
  RecoMode,
  SeriesInfo,
  Stats,
  Tag,
  TagKind,
} from "../../../shared/types";

export type ApiMode = "server" | "vorschau" | "unbekannt";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: "fehler" | "warnung" = "fehler",
  ) {
    super(message);
  }
}

let mode: ApiMode = "unbekannt";
let coverBase = "/covers";
let kiBereit = false;

export const apiMode = () => mode;
export const kiVerfuegbar = () => kiBereit;
export const coverUrl = (isbn: string | null, size: "s" | "m" | "spine") =>
  isbn ? `${coverBase}/${size}/${isbn}.jpg` : null;

interface Envelope<T> {
  ok: boolean;
  status: "ok" | "warnung" | "fehler";
  message?: string;
  data?: T;
}

/** Ergebnis eines schreibenden Aufrufs — die Oberfläche zeigt genau das an. */
export interface Result<T> {
  data: T;
  status: "ok" | "warnung";
  message?: string;
}

/** Woher die API kommt.
 *
 *  Lokal leer: Vite proxyt `/api` auf den Worker an Port 8788 (vite.config.ts).
 *  Im Betrieb steht hier beim Bauen die Worker-Domain in `VITE_API_URL`, denn
 *  auf der Pages-Domain liegt kein Worker — ein relativer Aufruf ginge dort ins
 *  Leere. Ein etwaiger Schrägstrich am Ende wird entfernt, damit aus der
 *  Verkettung nicht `//api` wird. */
const API_BASE = String(import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");

async function call<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  if (mode === "vorschau" && init && init.method && init.method !== "GET") {
    throw new ApiError(
      "Vorschau ohne Server: Änderungen können nicht gespeichert werden. " +
        "Starte den Worker (npm run dev im Ordner worker) und lade neu.",
    );
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        // Nur gesetzt, wenn die API ein gemeinsames Geheimnis verlangt
        // (VITE_API_TOKEN beim Bauen, API_TOKEN als Worker-Secret).
        ...(import.meta.env.VITE_API_TOKEN
          ? { "x-regal-token": String(import.meta.env.VITE_API_TOKEN) }
          : {}),
        ...(init?.headers || {}),
      },
    });
  } catch (err) {
    throw new ApiError(`Server nicht erreichbar (${(err as Error).message}).`);
  }

  let body: Envelope<T>;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    throw new ApiError(`Server antwortet unverständlich (HTTP ${res.status}).`);
  }
  if (!body.ok)
    throw new ApiError(body.message || `Fehlgeschlagen (HTTP ${res.status}).`);
  return {
    data: body.data as T,
    status: body.status === "warnung" ? "warnung" : "ok",
    message: body.message,
  };
}

const post = <T>(path: string, payload?: unknown) =>
  call<T>(path, {
    method: "POST",
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
const patch = <T>(path: string, payload: unknown) =>
  call<T>(path, { method: "PATCH", body: JSON.stringify(payload) });
const del = <T>(path: string) => call<T>(path, { method: "DELETE" });

/* ─────────────────────────── Start / Vorschau ───────────────────────────── */

interface Health {
  up: boolean;
  ki: boolean;
  bestand: Record<string, number>;
  coverBase: string;
}

export async function connect(): Promise<ApiMode> {
  try {
    // Über API_BASE, nicht relativ: Auf der veröffentlichten Seite liegt kein
    // Worker, und Cloudflare Pages beantwortet unbekannte Pfade mit der
    // index.html und HTTP 200. Ein relativer Aufruf sähe also "erfolgreich"
    // aus und die App fiele stumm in den Vorschau-Modus zurück.
    const res = await fetch(`${API_BASE}/api/health`);
    if (res.ok) {
      const body = (await res.json()) as Envelope<Health>;
      // Nicht nur auf den Status verlassen — nur eine echte Antwort des
      // Workers zählt. HTML mit Status 200 ist kein laufender Server.
      if (body?.ok && body.data?.up) {
        mode = "server";
        kiBereit = !!body.data.ki;
        coverBase = body.data.coverBase || "/covers";
        return mode;
      }
    }
  } catch {
    /* kein Worker — unten weiter */
  }
  mode = "vorschau";
  return mode;
}

/** Notbestand aus books.json: reicht zum Anschauen, nicht zum Arbeiten. */
async function vorschauBestand(who: Owner): Promise<Book[]> {
  const res = await fetch(`/books.json?v=${Date.now()}`);
  if (!res.ok) throw new ApiError("Auch books.json ist nicht erreichbar.");
  const raw = (await res.json()) as Record<string, unknown>[];
  return raw
    .filter((b) => b.who === who)
    .map((b, i) => ({
      id: i + 1,
      owner: who,
      isbn: (b.isbn as string) ?? null,
      title: (b.title as string) ?? "",
      author: (b.author as string) ?? "",
      link_key: "",
      series: null,
      series_no: null,
      series_lock: 0,
      status: (b.status as Book["status"]) ?? "read",
      rating: null,
      stars: (b.stars as number) ?? null,
      stars_seen: 0,
      fav: b.fav ? 1 : 0,
      read_year: (b.read_year as string) ?? null,
      read_date: null,
      pages: (b.pages as number) ?? null,
      pub_year: (b.year as string) ?? null,
      publisher: null,
      language: null,
      description: null,
      desc_source: null,
      notes: null,
      cover: b.cover ? 1 : 0,
      cover_key: (b.isbn as string) ?? null,
      cover_ratio: (b.ratio as number) ?? null,
      cover_avg: (b.avg as string) ?? null,
      cover_edge: (b.edge as string) ?? null,
      cover_dark: b.dark ? 1 : 0,
      source: "vorschau",
      source_id: null,
      created_at: "",
      updated_at: "",
      deleted_at: null,
      tags: [],
      lists: [],
      also: !!b.also,
    })) as Book[];
}

/* ──────────────────────────────── Regal ─────────────────────────────────── */

export interface LibraryPayload {
  books: Book[];
  lists: BookList[];
  series: SeriesInfo[];
}

export async function library(who: Owner): Promise<LibraryPayload> {
  if (mode === "vorschau") {
    return { books: await vorschauBestand(who), lists: [], series: [] };
  }
  return (await call<LibraryPayload>(`/library/${who}`)).data;
}

export const bookDetail = (who: Owner, id: number) =>
  call<{ book: Book; noteHistory: { notes: string; saved_at: string }[] }>(
    `/book/${who}/${id}`,
  );

export const saveBook = (who: Owner, id: number, patchData: Partial<Book>) =>
  patch<Book>(`/book/${who}/${id}`, patchData);

export const addBook = (who: Owner, data: Partial<Book> & { title: string }) =>
  post<Book>(`/book/${who}`, data);

export const removeBook = (who: Owner, id: number) =>
  del<null>(`/book/${who}/${id}`);
export const restoreBook = (who: Owner, id: number) =>
  post<null>(`/book/${who}/${id}/restore`);
export const enrichBook = (who: Owner, id: number) =>
  post<Book>(`/book/${who}/${id}/enrich`);
export const restoreNotes = (who: Owner, id: number, saved_at: string) =>
  post<Book>(`/book/${who}/${id}/notes/restore`, { saved_at });

/* ──────────────────────── Listen, Tags, Auswertung ──────────────────────── */

export const lists = (who: Owner) => call<BookList[]>(`/lists/${who}`);
export const addList = (who: Owner, name: string) =>
  post<BookList>(`/lists/${who}`, { name });
export const removeList = (who: Owner, id: number) =>
  del<null>(`/lists/${who}/${id}`);
export const setListMember = (
  who: Owner,
  listId: number,
  bookId: number,
  member: boolean,
) => post<null>(`/lists/${who}/${listId}/books/${bookId}`, { member });

export const tags = (who: Owner) =>
  call<{
    cloud: { name: string; kind: TagKind; count: number }[];
    vocabulary: Record<string, string[]>;
  }>(`/tags/${who}`);
export const setTags = (
  who: Owner,
  bookId: number,
  add: string[],
  remove: string[],
) => post<Tag[]>(`/book/${who}/${bookId}/tags`, { add, remove });
export const runTagging = (who: Owner, limit = 20) =>
  post<{ getaggt: number; offen: number }>(`/tags/${who}/run`, { limit });

export const stats = (who: Owner) => call<Stats>(`/stats/${who}`);
export const ritual = (who: Owner) =>
  call<{ books: Book[]; offen: number }>(`/ritual/${who}`);
export const scanSeriesNow = (who: Owner) =>
  post<{ geaendert: number }>(`/series/${who}/scan`);
export const together = () =>
  call<{ key: string; sarah: Book; mario: Book }[]>(`/together`);

/* ───────────────────────────── Empfehlungen ─────────────────────────────── */

export const recommend = (
  who: Owner,
  body: {
    mode: RecoMode;
    wanted?: string[];
    unwanted?: string[];
    limit?: number;
  },
) =>
  post<{
    mode: RecoMode;
    quelle: "ki" | "lokal";
    vorschlaege: Recommendation[];
  }>(`/recommend/${who}`, body);

export const recommendations = (who: Owner) =>
  call<Recommendation[]>(`/recommendations/${who}`);
export const judgeRecommendation = (
  who: Owner,
  id: number,
  body: { hide?: boolean; save?: boolean },
) => patch<null>(`/recommendations/${who}/${id}`, body);

/* ─────────────────────── Werkstatt, Import, Papierkorb ──────────────────── */

export interface Workshop {
  ohneCover: Book[];
  ohneIsbn: Book[];
  ohneText: Book[];
  ohneTags: Book[];
  ohneJahr: Book[];
  gesamt: number;
  ki: boolean;
}
export const workshop = (who: Owner) => call<Workshop>(`/workshop/${who}`);

export interface LookupResult {
  isbn: string | null;
  title: string;
  author: string;
  pages: number | null;
  pub_year: string | null;
  publisher: string | null;
  description: string | null;
  tags: string[];
  cover_url: string | null;
  source: string;
}
export const lookup = (body: {
  isbn?: string;
  title?: string;
  author?: string;
}) => post<LookupResult | null>(`/lookup`, body);

export interface ImportRow {
  isbn?: string | null;
  title: string;
  author?: string;
  status?: string;
  read_year?: string | null;
  fav?: boolean;
  stars?: number | null;
  pages?: number | null;
  pub_year?: string | null;
  lists?: string[];
  source_id?: string | null;
}
export interface ImportDiff {
  neu: ImportRow[];
  bekannt: { row: ImportRow; id: number }[];
  verdacht: { row: ImportRow; kandidat: string; id: number; naehe: number }[];
  fehlerhaft: { row: ImportRow; grund: string }[];
  bestand: number;
}
export const analyzeImport = (who: Owner, rows: ImportRow[]) =>
  post<ImportDiff>(`/import/${who}/analyze`, { rows });
export const applyImport = (
  who: Owner,
  rows: ImportRow[],
  filename: string,
  format: string,
) =>
  post<{ added: number; probleme: string[] }>(`/import/${who}/apply`, {
    rows,
    filename,
    format,
  });

export const trash = (who: Owner) =>
  call<{ books: Book[]; lists: BookList[]; tage: number }>(`/trash/${who}`);
export const purgeTrash = (who: Owner) =>
  post<{ geloescht: number }>(`/trash/${who}/purge`);
